import { useMemo, useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { mermaidComponents } from './markdown-mermaid.js';
import { useWorkspaceFile } from '../hooks/use-workspace.js';
import { useToast } from '../hooks/use-toast.js';
import { useTokenizedLines } from '../hooks/use-shiki.js';

interface FileViewerProps {
  serverId: string;
  path: string | null;
  onRefresh: () => void;
}

const LANG_MAP: Record<string, string> = {
  ts: 'typescript', tsx: 'typescript', js: 'javascript', jsx: 'javascript',
  py: 'python', rb: 'ruby', go: 'go', rs: 'rust',
  hcl: 'hcl', tf: 'hcl', tfvars: 'hcl', yml: 'yaml', yaml: 'yaml',
  json: 'json', css: 'css', html: 'html', md: 'markdown',
  sh: 'bash', bash: 'bash', zsh: 'bash',
  toml: 'toml', xml: 'xml', sql: 'sql',
  dockerfile: 'dockerfile', makefile: 'makefile',
  php: 'php', java: 'java', c: 'c', h: 'c', cpp: 'cpp', hpp: 'cpp',
};

function getLanguage(path: string): string | undefined {
  const filename = path.split('/').pop()?.toLowerCase() ?? '';
  if (filename === 'dockerfile') return 'dockerfile';
  if (filename === 'makefile') return 'makefile';
  const ext = filename.split('.').pop();
  return ext ? LANG_MAP[ext] : undefined;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function FileViewer({ serverId, path, onRefresh }: FileViewerProps) {
  const { data, isLoading, isError, error } = useWorkspaceFile(serverId, path);
  const language = useMemo(() => (path ? getLanguage(path) : undefined), [path]);
  const isMarkdown = language === 'markdown';
  const [preview, setPreview] = useState(true);
  const content = data?.content ?? '';
  const highlightLang = isMarkdown && preview ? undefined : language;
  const tokens = useTokenizedLines(content, highlightLang);

  if (!path) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-8" data-testid="file-viewer-empty">
        <svg className="h-10 w-10" style={{ color: 'var(--color-text-3)', opacity: 0.5 }} fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
        </svg>
        <p className="text-sm" style={{ color: 'var(--color-text-3)' }}>Select a file to view its contents</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full flex-col" data-testid="file-viewer">
        <div className="px-4 py-2" style={{ borderBottom: '1px solid var(--color-border)' }}>
          <div className="h-4 w-48 animate-pulse rounded" style={{ backgroundColor: 'var(--color-surface-3)' }} />
        </div>
        <div className="flex-1 space-y-2 p-4">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div key={i} className="h-4 animate-pulse rounded" style={{ width: `${30 + (i * 7) % 50}%`, backgroundColor: 'var(--color-surface-2)' }} />
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    const message = error.message;
    const isTooLarge = message.includes('too large') || message.includes('1 MB');
    return (
      <div className="flex h-full flex-col" data-testid="file-viewer">
        <FileHeader path={path} onRefresh={onRefresh} />
        <div className="flex flex-1 items-center justify-center p-4">
          <div className="text-center text-sm" style={{ color: isTooLarge ? 'var(--color-warn)' : 'var(--color-error)' }}>
            {isTooLarge ? (
              <>
                <svg className="mx-auto mb-2 h-8 w-8" style={{ color: 'var(--color-warn)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                </svg>
                File exceeds the 1 MB size limit
              </>
            ) : message}
          </div>
        </div>
      </div>
    );
  }

  if (data?.binary) {
    return (
      <div className="flex h-full flex-col" data-testid="file-viewer">
        <FileHeader path={path} size={data.size} mimeType={data.mimeType} onRefresh={onRefresh} />
        <div className="flex flex-1 items-center justify-center p-4 text-sm" style={{ color: 'var(--color-text-3)' }}>
          Binary file ({data.mimeType}, {formatSize(data.size)})
        </div>
      </div>
    );
  }

  const lines = content.split('\n');

  return (
    <div className="flex h-full flex-col" data-testid="file-viewer">
      <FileHeader path={path} size={data?.size} mimeType={data?.mimeType} language={language} content={data?.content} onRefresh={onRefresh} isMarkdown={isMarkdown} preview={preview} onTogglePreview={() => setPreview((p) => !p)} />
      {isMarkdown && preview ? (
        <div className="prose-event flex-1 overflow-auto p-6 text-sm" style={{ color: 'var(--color-text)' }}>
          <Markdown remarkPlugins={[remarkGfm]} components={mermaidComponents}>{content}</Markdown>
        </div>
      ) : (
        <div className="flex-1 overflow-auto">
          <pre className="text-xs leading-5">
            <table className="w-full border-collapse">
              <tbody>
                {lines.map((line, idx) => (
                  <tr key={idx} className="transition-colors" style={{ '--hover-bg': 'var(--color-surface-2)' } as React.CSSProperties}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--color-surface-2)')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                  >
                    <td className="w-12 select-none px-3 text-right font-mono" style={{ borderRight: '1px solid var(--color-border)', color: 'var(--color-text-3)' }}>
                      {idx + 1}
                    </td>
                    <td className="whitespace-pre px-4 font-mono" style={{ color: 'var(--color-text)' }}>
                      {tokens?.[idx]
                        ? tokens[idx].map((t, j) => <span key={j} style={t.color ? { color: t.color } : undefined}>{t.content}</span>)
                        : line}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </pre>
        </div>
      )}
    </div>
  );
}

function CopyFileButton({ content }: { content?: string }) {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();
  if (!content) return null;
  return (
    <button
      type="button"
      onClick={() => { navigator.clipboard.writeText(content); toast('Copied to clipboard', 'success'); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
      className="shrink-0 rounded p-1"
      style={{ color: copied ? 'var(--color-success)' : 'var(--color-text-3)' }}
      title="Copy file contents"
      aria-label="Copy file contents"
    >
      {copied ? (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
        </svg>
      ) : (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9.75a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184" />
        </svg>
      )}
    </button>
  );
}

function DownloadFileButton({ content, path, mimeType }: { content?: string; path: string; mimeType?: string }) {
  const [downloaded, setDownloaded] = useState(false);
  if (!content) return null;
  const filename = path.split('/').pop() ?? 'download';
  return (
    <button
      type="button"
      onClick={() => {
        const blob = new Blob([content], { type: mimeType ?? 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
        setDownloaded(true);
        setTimeout(() => setDownloaded(false), 2000);
      }}
      className="shrink-0 rounded p-1"
      style={{ color: downloaded ? 'var(--color-success)' : 'var(--color-text-3)' }}
      title="Download file"
      aria-label="Download file"
    >
      {downloaded ? (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
        </svg>
      ) : (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
        </svg>
      )}
    </button>
  );
}

function FileHeader({
  path,
  size,
  mimeType,
  language,
  content,
  onRefresh,
  isMarkdown,
  preview,
  onTogglePreview,
}: {
  path: string;
  size?: number;
  mimeType?: string;
  language?: string;
  content?: string;
  onRefresh: () => void;
  isMarkdown?: boolean;
  preview?: boolean;
  onTogglePreview?: () => void;
}) {
  return (
    <div className="flex items-center justify-between px-4 py-2" style={{ borderBottom: '1px solid var(--color-border)', backgroundColor: 'var(--color-surface-2)' }}>
      <div className="flex items-center gap-3 overflow-hidden">
        <span className="truncate font-mono text-xs" style={{ color: 'var(--color-text-2)' }}>{path}</span>
        {language && (
          <span className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium" style={{ backgroundColor: 'var(--color-surface-3)', color: 'var(--color-text-3)' }}>
            {language}
          </span>
        )}
        {size !== undefined && (
          <span className="shrink-0 text-[10px]" style={{ color: 'var(--color-text-3)' }}>{formatSize(size)}</span>
        )}
        {mimeType && (
          <span className="hidden shrink-0 text-[10px] sm:inline" style={{ color: 'var(--color-text-3)' }}>{mimeType}</span>
        )}
      </div>
      <div className="flex items-center gap-1">
        {isMarkdown && onTogglePreview && (
          <button
            type="button"
            onClick={onTogglePreview}
            className="shrink-0 rounded px-2 py-0.5 text-[10px] font-medium"
            style={{ backgroundColor: preview ? 'var(--color-brand-dim)' : 'var(--color-surface-3)', color: preview ? 'var(--color-brand)' : 'var(--color-text-3)' }}
            title={preview ? 'Show source' : 'Show preview'}
            aria-label={preview ? 'Show source' : 'Show preview'}
          >
            {preview ? 'Preview' : 'Source'}
          </button>
        )}
        <CopyFileButton content={content} />
        <DownloadFileButton content={content} path={path} mimeType={mimeType} />
        <button
          type="button"
          onClick={onRefresh}
          className="shrink-0 rounded p-1"
          style={{ color: 'var(--color-text-3)' }}
          title="Refresh file"
          aria-label="Refresh file"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.992 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}
